import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { getStoredHorseHandle, setStoredHorseHandle } from '@theme/try-on-storage';

/**
 * Try-on gallery (the collection.try-on view): a persistent horse-color
 * selector that re-renders every product card's horse layer. Category,
 * sort and page are server-rendered by Shopify (real collection pagination);
 * this component just swaps the section in place when they change. Each
 * card holds a loader overlay until its first pad+horse composite has
 * loaded, and again while a newly picked horse downloads.
 *
 * @typedef {object} TryOnGalleryRefs
 * @property {HTMLElement} [main]
 * @property {HTMLElement} [status]
 *
 * @extends {Component<TryOnGalleryRefs>}
 */
export class TryOnGalleryComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('popstate', this.#onPopState);
    this.hydrate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('popstate', this.#onPopState);
  }

  /**
   * @param {PointerEvent} event
   */
  selectHorse(event) {
    const button = /** @type {HTMLButtonElement} */ (event.target);
    if (!button.matches('.try-on-gallery__horse-swatch') || button.classList.contains('is-selected')) return;

    this.applyHorseSelection(button, { persist: true });
  }

  /**
   * @param {HTMLElement} button
   * @param {{ persist: boolean }} options
   */
  applyHorseSelection(button, { persist }) {
    const handle = button.dataset.horseHandle;
    const imageUrl = button.dataset.imageUrl;
    const imageSrcset = button.dataset.imageSrcset ?? '';
    if (!handle || !imageUrl) return;

    for (const swatch of this.querySelectorAll('.try-on-gallery__horse-swatch')) {
      const isSelected = swatch === button;
      swatch.classList.toggle('is-selected', isSelected);
      swatch.setAttribute('aria-pressed', String(isSelected));
    }

    this.#horseHandle = handle;
    if (persist) setStoredHorseHandle(handle);

    const swapId = ++this.#swapId;

    // Card images stay alt="" (decorative) — the card link's text already names the product.
    /** @param {HTMLImageElement} image */
    const setHorse = (image) => {
      image.srcset = imageSrcset;
      image.src = imageUrl;
      image.dataset.horseHandle = handle;
    };

    /** @type {HTMLImageElement[]} */
    const pending = [];
    for (const image of this.querySelectorAll('.try-on-gallery__card-horse-image')) {
      if (!(image instanceof HTMLImageElement)) continue;
      const stage = image.closest('.try-on-gallery__card-stage');

      // A card still on its first load already has its loader up, and revealWhenLoaded waits on
      // whatever src is current, so just repoint it.
      if (!stage?.classList.contains('is-ready')) {
        if (image.dataset.horseHandle !== handle) setHorse(image);
        continue;
      }

      // Also clears a loader left up by a superseded swap back to the horse this card already shows.
      const needsSwap = image.dataset.horseHandle !== handle;
      stage.classList.toggle('is-loading', needsSwap);
      if (needsSwap) pending.push(image);
    }
    if (pending.length === 0) return;

    // Every card shares one srcset/sizes, so a single preload warms the cache for all of them, and
    // the current composites stay up (under the delayed loader) until it lands.
    const preload = new Image();
    preload.sizes = pending[0].sizes;
    preload.srcset = imageSrcset;
    preload.src = imageUrl;

    whenComplete(preload).then(() => {
      if (swapId !== this.#swapId) return;

      for (const image of pending) {
        setHorse(image);
        // Offscreen-page cards are lazy and won't fetch until shown, so their loader waits until then.
        whenComplete(image).then(() => {
          if (swapId === this.#swapId) image.closest('.try-on-gallery__card-stage')?.classList.remove('is-loading');
        });
      }
    });
  }

  #swapId = 0;

  /**
   * Category links, pagination links (via pagination-controls' on_click_handler, which passes the link's query
   * params first) and the category select all land here.
   *
   * @param {...(MouseEvent | object)} args
   */
  navigate(...args) {
    const event = /** @type {MouseEvent} */ (args.at(-1));
    const link = /** @type {HTMLAnchorElement} */ (event.target);
    // Let modified clicks open a new tab/window as usual.
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (!(link instanceof HTMLAnchorElement)) return;

    event.preventDefault();
    const isPageChange = link.closest('.pagination') !== null;
    this.goTo(new URL(link.href), { scroll: isPageChange });
  }

  /**
   * @param {Event} event
   */
  selectCategoryFromSelect(event) {
    const select = /** @type {HTMLSelectElement} */ (event.target);
    if (select.value) this.goTo(new URL(select.value, location.href));
  }

  /**
   * @param {Event} event
   */
  selectSort(event) {
    const select = /** @type {HTMLSelectElement} */ (event.target);
    const url = new URL(location.href);
    url.searchParams.set('sort_by', select.value);
    url.searchParams.delete('page');
    this.goTo(url);
  }

  /**
   * Pushes the new URL and re-renders just this section for it — the same pattern the theme's collection filters
   * use — so the horse bar, selected horse and scroll position survive a category/sort/page change.
   *
   * @param {URL} url
   * @param {{ scroll?: boolean }} [options]
   */
  goTo(url, { scroll = false } = {}) {
    // Keep refreshes/shared links on this view rather than the regular collection page.
    url.searchParams.set('view', 'try-on');
    url.searchParams.delete('section_id');
    history.pushState({ tryOn: true }, '', url.toString());
    this.renderCurrentUrl({ scroll });
  }

  /**
   * @param {{ scroll?: boolean }} [options]
   */
  async renderCurrentUrl({ scroll = false } = {}) {
    const sectionId = this.getAttribute('section-id');
    if (!sectionId) {
      location.reload();
      return;
    }

    this.setAttribute('aria-busy', 'true');
    try {
      await sectionRenderer.renderSection(sectionId);
    } catch {
      // A superseded render resolves (with the newer one's result) rather than throwing, so this is a real failure.
      location.reload();
      return;
    }
    this.removeAttribute('aria-busy');

    this.hydrate();
    if (scroll) this.refs.main?.scrollIntoView({ block: 'start' });

    const { main, status } = this.refs;
    if (status) {
      const items = Number(main?.dataset.items ?? 0);
      const page = Number(main?.dataset.page ?? 1);
      const pages = Number(main?.dataset.pages ?? 1);
      status.textContent =
        pages > 1 ? `Page ${page} of ${pages}, ${items} pads` : `Showing ${items} ${items === 1 ? 'pad' : 'pads'}`;
    }
  }

  /** Back/forward between gallery URLs re-renders in place instead of leaving stale cards up. */
  #onPopState = () => {
    if (new URL(location.href).searchParams.get('view') === 'try-on') this.renderCurrentUrl();
  };

  /**
   * Brings freshly rendered markup (initial load or a section re-render) up to date with client-side state: the
   * chosen horse, card loaders, and the sidebar counts the server can't compute for other categories.
   */
  hydrate() {
    const handle = this.#horseHandle ?? getStoredHorseHandle();
    if (handle) {
      const button = this.querySelector(`.try-on-gallery__horse-swatch[data-horse-handle="${CSS.escape(handle)}"]`);
      if (button instanceof HTMLElement) this.applyHorseSelection(button, { persist: false });
    }

    for (const stage of this.querySelectorAll('.try-on-gallery__card-stage')) {
      if (stage instanceof HTMLElement) revealWhenLoaded(stage);
    }

    this.fillCategoryCounts();
  }

  /** @type {Map<string, Promise<number | null>>} */
  #counts = new Map();

  /**
   * Fetches each category's try-on-ready count once per page view (sections/try-on-gallery-count.liquid) and
   * hides categories that have none. The current category's count is server-rendered and left alone.
   */
  fillCategoryCounts() {
    for (const link of this.querySelectorAll('.try-on-gallery__category[data-count-url]')) {
      if (!(link instanceof HTMLElement)) continue;
      const countElement = link.querySelector('.try-on-gallery__category-count');
      const url = link.dataset.countUrl;
      if (!countElement || !url) continue;

      const isCurrent = link.getAttribute('aria-current') === 'page';
      if (isCurrent && countElement.textContent?.trim()) {
        this.#counts.set(url, Promise.resolve(Number(countElement.textContent)));
        continue;
      }

      let count = this.#counts.get(url);
      if (!count) {
        count = fetchCount(url);
        this.#counts.set(url, count);
      }

      count.then((value) => {
        if (value === null) return;
        countElement.textContent = String(value);
        if (!isCurrent) link.hidden = value === 0;
        this.syncSelectOption(link, value);
      });
    }
  }

  /**
   * Mirrors a sidebar count into the mobile select's matching option, e.g. "Pleasure (8)".
   *
   * @param {HTMLElement} link
   * @param {number} value
   */
  syncSelectOption(link, value) {
    const href = link.getAttribute('href');
    const option = [...this.querySelectorAll('.try-on-gallery__category-select option')].find(
      (option) => option instanceof HTMLOptionElement && option.value === href,
    );
    if (!(option instanceof HTMLOptionElement)) return;

    option.dataset.label ??= option.textContent?.trim() ?? '';
    option.textContent = `${option.dataset.label} (${value})`;
    option.hidden = value === 0 && !option.selected;
  }

  /** @type {string | undefined} */
  #horseHandle;
}

/**
 * @param {string} url
 * @returns {Promise<number | null>} The count, or null when the try-on filter isn't set up.
 */
async function fetchCount(url) {
  try {
    const response = await fetch(url);
    if (!response.ok) return null;
    const html = await response.text();
    const match = html.match(/data-try-on-count="(\d+)"/);
    return match ? Number(match[1]) : null;
  } catch {
    return null;
  }
}

/**
 * Clears a card's initial loader once both of its layers have loaded, so the
 * pad never shows alone. No timeout: cards are lazy, so ones off the current
 * page or below the fold just stay pending until they're shown and fetched.
 *
 * @param {HTMLElement} stage
 */
async function revealWhenLoaded(stage) {
  const images = /** @type {HTMLImageElement[]} */ ([...stage.querySelectorAll('img')]);
  await Promise.all(images.map(whenComplete));
  stage.classList.add('is-ready');
  stage.classList.remove('is-loading');
}

/**
 * Resolves once an image's current request settles (load or error), or
 * immediately if it already has.
 *
 * @param {HTMLImageElement} image
 * @returns {Promise<void>}
 */
function whenComplete(image) {
  if (image.complete) return Promise.resolve();
  return new Promise((resolve) => {
    image.addEventListener('load', () => resolve(), { once: true });
    image.addEventListener('error', () => resolve(), { once: true });
  });
}

if (!customElements.get('try-on-gallery-component')) {
  customElements.define('try-on-gallery-component', TryOnGalleryComponent);
}
